/**
 * @fileoverview Zod schemas for text chunking utilities.
 *
 * These schemas are used for validating chunk metadata across the application,
 * particularly in agents that process large documents in chunks.
 */

import { z } from 'zod';

/**
 * Metadata about a single chunk's position within the full document.
 * Used to provide context to agents processing chunked content.
 */
export const ChunkInfoSchema = z.object({
  /** Zero-based index of this chunk */
  chunkIndex: z.number().int().min(0).describe('Zero-based index of this chunk in the sequence.'),

  /** Total number of chunks the document was split into */
  totalChunks: z.number().int().min(1).describe('Total number of chunks the document was split into.'),

  /** Whether this is the first chunk (no previous context) */
  isFirstChunk: z.boolean().describe('True if this is the first chunk in the sequence.'),

  /** Whether this is the last chunk (final pass) */
  isLastChunk: z.boolean().describe('True if this is the last chunk in the sequence.'),

  /**
   * Character offset where this chunk's core content starts in the original document.
   * Excludes leading overlap.
   */
  coreStartOffset: z.number().int().min(0).describe('Character offset where core content starts in original document.'),

  /**
   * Character offset where this chunk's core content ends in the original document.
   * Excludes trailing overlap.
   */
  coreEndOffset: z.number().int().min(0).describe('Character offset where core content ends in original document.'),
}).describe('Metadata about a chunk position within a larger document.');

export type ChunkInfo = z.infer<typeof ChunkInfoSchema>;

/**
 * A chunk of text with its associated metadata.
 * Returned by the chunkText utility function.
 */
export const ChunkResultSchema = z.object({
  /** The chunk text content (including any overlap) */
  text: z.string().describe('The chunk text content including any overlap from adjacent chunks.'),

  /** Metadata about the chunk position */
  chunkInfo: ChunkInfoSchema.describe('Metadata about the chunk position within the document.'),
}).describe('A chunk of text with its position metadata.');

export type ChunkResult = z.infer<typeof ChunkResultSchema>;
